import clsx from 'clsx'
import { useCallback, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Download } from 'lucide-react'
import { reportBtnGhost, reportSegment, reportSegmentBtn } from '../ui'
import { AlertFiltersBar } from './AlertFiltersBar'
import {
  ALERT_VIEWS, filtersFromSearch, filtersToSearch, PAGE_RESETTING_KEYS, toFacetParams,
  type AlertFilters as AlertFilterState, type AlertView,
} from './alertFilters'
import type { AlertSite, FacetRow, FacetsEnvelope } from './alertTypes'
import { useAlertAnalytics } from './useAlertAnalytics'
import { AlertListView } from './views/AlertListView'
import { DayNightView } from './views/DayNightView'
import { DriversView } from './views/DriversView'
import { LocationsView } from './views/LocationsView'
import { OverviewView } from './views/OverviewView'
import { TrendsView } from './views/TrendsView'
import { VehiclesView } from './views/VehiclesView'
import { ExportContext, type ExportFn, type LocationsMapSlot } from './views/viewKit'

export interface AlertsReportProps {
  /** Sites this user may report on, first = default. ConnectedAlertsReport computes the
   *  granted list from the session; the default is the v1 pair so the report works
   *  standalone (and in tests). */
  sites?: readonly AlertSite[]
  /** Map slot for the Locations tab (map/LocationMap). */
  locationsMap?: LocationsMapSlot
  /** Drawer slot: rendered whenever `?alert=<id>` is set, with a close callback that
   *  clears it (AlertTripDrawer). */
  alertDrawer?: (alertId: string, onClose: () => void) => ReactNode
}

const TABS: { id: AlertView; label: string; hint: string }[] = [
  { id: 'overview', label: 'Overview', hint: 'Is this channel signal, and who repeats -- the morning read.' },
  { id: 'drivers', label: 'Drivers', hint: 'Who the alerts attach to, by TIMS spell at the moment they fired.' },
  { id: 'vehicles', label: 'Vehicles', hint: 'Which trucks fire, and whether it follows the truck or the driver.' },
  { id: 'locations', label: 'Locations', hint: 'Where alerts cluster: hubs, en-route bins, overspeed zones.' },
  { id: 'daynight', label: 'Day / Night', hint: 'Does the fleet behave differently after 22:00 IST?' },
  { id: 'trends', label: 'Trends', hint: 'Alerts over time, split by type, severity, driver, vehicle, place or shift.' },
  { id: 'list', label: 'Alert list', hint: 'Every alert in the window -- click one to see the trip it happened in.' },
]

const DEFAULT_SITES: readonly AlertSite[] = ['ultratech', 'wondercement']

/**
 * Reports > Alerts. One filter bar, seven sub-tabs, the URL as the only state: every
 * knob round-trips through `?view=&site=&preset=&severity=&kind=&driver=&alert=…` with
 * `replace: true`, so a pasted link restores the exact report and the browser's back
 * button is not flooded with one entry per chip click. Data is fetched on demand only --
 * each tab asks for its own aggregate when its inputs change, and nothing here polls.
 */
export function AlertsReport({ sites: sitesProp, locationsMap, alertDrawer }: AlertsReportProps): JSX.Element {
  const [sp, setSp] = useSearchParams()
  // Stabilise on content, not identity: a caller passing a literal array each render
  // must not re-create the filter object (and refetch) every time.
  const sitesKey = (sitesProp ?? DEFAULT_SITES).join(',')
  const sites = useMemo(() => sitesKey.split(',').filter(Boolean) as AlertSite[], [sitesKey])
  const defaultSite: AlertSite = sites[0] ?? 'ultratech'

  const filters = useMemo(() => filtersFromSearch(sp, { site: defaultSite, sites }), [sp, defaultSite, sites])

  const setFilters = useCallback((patch: Partial<AlertFilterState>) => {
    setSp((prev) => {
      const cur = filtersFromSearch(prev, { site: defaultSite, sites })
      const next: AlertFilterState = { ...cur, ...patch }
      // Anything that changes the row set invalidates the list page; a tab change also
      // drops the selected alert, which belongs to the list.
      if (PAGE_RESETTING_KEYS.some((k) => k in patch)) next.page = 0
      if ('view' in patch && patch.view !== cur.view) { next.page = 0; next.alert = null }
      return filtersToSearch(next, { site: defaultSite })
    }, { replace: true })
  }, [setSp, defaultSite, sites])

  const facetParams = useMemo(() => toFacetParams(filters), [filters])
  const { envelope: facets, status: facetsStatus } = useAlertAnalytics<FacetRow, FacetsEnvelope>('/facets', facetParams)

  // "Export this view" lives in the filter bar; the active view hands over its exporter.
  const [exporter, setExporter] = useState<{ run: ExportFn } | null>(null)
  const registerExport = useCallback((fn: ExportFn | null) => setExporter(fn ? { run: fn } : null), [])
  const [exportState, setExportState] = useState<'idle' | 'busy' | 'error'>('idle')
  const [exportError, setExportError] = useState<string | null>(null)
  const runExport = async () => {
    if (!exporter) return
    setExportState('busy')
    setExportError(null)
    try {
      await exporter.run()
      setExportState('idle')
    } catch (e) {
      setExportState('error')
      setExportError(e instanceof Error ? e.message : 'Excel export failed')
    }
  }

  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return
    e.preventDefault()
    const i = ALERT_VIEWS.indexOf(filters.view)
    const n = ALERT_VIEWS.length
    const nextIdx = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n
    const next = ALERT_VIEWS[nextIdx]!
    setFilters({ view: next })
    ;(e.currentTarget.querySelector<HTMLButtonElement>(`[data-view="${next}"]`))?.focus()
  }

  const activeTab = TABS.find((t) => t.id === filters.view) ?? TABS[0]!
  const viewProps = { filters, setFilters, facets: facets ?? null }
  const panelId = `alerts-panel-${filters.view}`
  const tabId = (v: AlertView) => `alerts-tab-${v}`

  return (
    <div className="flex flex-col gap-3 px-1 pb-6">
      <div
        className="sticky top-0 z-20 -mx-1 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[rgba(15,23,42,0.07)] bg-white/85 px-3 py-2 shadow-sm backdrop-blur"
      >
        <div
          role="tablist"
          aria-label="Alert report views"
          className={clsx(reportSegment, 'flex-wrap')}
          onKeyDown={onTabKey}
        >
          {TABS.map((t) => {
            const active = t.id === filters.view
            return (
              <button
                key={t.id}
                id={tabId(t.id)}
                data-view={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={panelId}
                tabIndex={active ? 0 : -1}
                onClick={() => setFilters({ view: t.id })}
                className={clsx(reportSegmentBtn(active), '!px-3 !py-1 !text-[12.5px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]')}
              >
                {t.label}
              </button>
            )
          })}
        </div>
        <p className="min-w-0 flex-1 truncate text-[12px] text-slate-500 sm:text-right">{activeTab.hint}</p>
      </div>

      <ExportContext.Provider value={registerExport}>
        <AlertFiltersBar
          filters={filters}
          onChange={setFilters}
          sites={sites}
          facets={facets ?? null}
          facetsFailed={facetsStatus === 'error'}
          actions={(
            <div className="flex items-center gap-2">
              {exportState === 'error' && exportError ? (
                <span role="alert" className="max-w-[16rem] truncate text-[11px] font-medium text-red-600" title={exportError}>{exportError}</span>
              ) : null}
              <button
                type="button"
                onClick={() => void runExport()}
                disabled={!exporter || exportState === 'busy'}
                title={exporter ? 'Download this tab as a styled Excel workbook' : 'Nothing loaded to export yet'}
                className={clsx(reportBtnGhost, 'h-8')}
              >
                <Download size={12} />
                {exportState === 'busy' ? 'Exporting…' : 'Export this view'}
              </button>
            </div>
          )}
        />

        <div id={panelId} role="tabpanel" aria-labelledby={tabId(filters.view)} className="animate-fade-in">
          {filters.view === 'overview' ? <OverviewView filters={filters} setFilters={setFilters} /> : null}
          {filters.view === 'drivers' ? <DriversView {...viewProps} /> : null}
          {filters.view === 'vehicles' ? <VehiclesView {...viewProps} /> : null}
          {filters.view === 'locations' ? <LocationsView {...viewProps} locationsMap={locationsMap} /> : null}
          {filters.view === 'daynight' ? <DayNightView {...viewProps} /> : null}
          {filters.view === 'trends' ? <TrendsView {...viewProps} /> : null}
          {filters.view === 'list' ? <AlertListView {...viewProps} /> : null}
        </div>
      </ExportContext.Provider>

      {filters.alert && alertDrawer ? alertDrawer(filters.alert, () => setFilters({ alert: null })) : null}
    </div>
  )
}
